import { memo, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import { Button } from "@mui/material";

import { useMobileVerificationContext } from "@zenith/context";
import { AppNavigation } from "@zenith/hooks";
import {
  MajorButtonComponent,
  PinInputComponent,
  PinRulesComponent,
} from "@zenith/components";
import {
  ApiResponseDTOEncryptionDTO,
  UsersApiService,
  UtilityApiService,
} from "@zenith/services/user";
import {
  DEFAULT_CHANNEL,
  PIN_LENGTH,
  PIN_MISMATCH_MESSAGE,
  REPEATED_REGEX,
  SEQUENTIAL_REGEX,
  SUCCESS_API_CODE,
} from "@zenith/data/constants";
import ChevronLeft from "@zenith/assets/icons/ChevronLeft";
import {
  FORGOT_PIN,
  MOBILE_VERIFICATION,
  PIN_CREATED,
} from "@zenith/routes/paths";
import {
  setConfirmPinSignUpValidations,
  setPinValidations,
} from "@zenith/features/Authentication/validations/AuthValidations";
import { encrypt } from "@zenith/utils";
import { resetTeamPasscode } from "@zenith/features/TeamManagement/api/teamPasscodeReset";
import { FormFieldErrorType } from "@zenith/types";

/* eslint-disable-next-line */
export interface CreateNewPinPageProps {}

interface LocationState {
  verificationCode?: string;
  /** BOAS-7938: the verified selfieJobId, an alternative proof to verificationCode. */
  jobId?: string;
  /** BOAS-8726: no registrationType on record - reset via the team endpoint. */
  noRegistrationType?: boolean;
  /** The X-MFA token the OTP verify returned, which authorises that reset. */
  xMfa?: string;
}

const CreateNewPinPage = memo(function CreateNewPinPage(
  props: CreateNewPinPageProps
) {
  const xApiKey = import.meta.env.VITE_X_API_KEY;

  const { navigateTo } = AppNavigation();
  const location = useLocation() as { state: LocationState };

  const {
    setFirstName,
    mobileVerificationContext: { countryCode, phoneNumber },
  } = useMobileVerificationContext();
  const [verificationCode, setVerificationCode] = useState<string>("");
  const [resetJobId, setResetJobId] = useState<string>("");
  const [resetXMfa, setResetXMfa] = useState<string>("");

  const [formData, setFormData] = useState<{
      pin: string;
      confirmPin: string;
    }>({
      pin: "",
      confirmPin: "",
    }),
    [newPin, setNewPin] = useState<Array<number | string>>(
      new Array(PIN_LENGTH).fill("")
    ),
    [newConfirmPin, setConfirmNewPin] = useState<Array<number | string>>(
      new Array(PIN_LENGTH).fill("")
    ),
    [confirmPinError, setConfirmPinError] = useState<FormFieldErrorType>({
      message: "",
      hasError: false,
    }),
    [pinError, setPinError] = useState<FormFieldErrorType>({
      message: "",
      hasError: false,
    });
  const [isSequential, setIsSequential] = useState(true);
  const [isRepeated, setIsRepeated] = useState(true);

  useEffect(() => {
    if (!countryCode && !phoneNumber) {
      navigateTo(FORGOT_PIN);
    }
    if (location.state?.verificationCode) {
      setVerificationCode(location.state.verificationCode);
    }
    if (location.state?.jobId) {
      setResetJobId(location.state.jobId);
    }
    if (location.state?.noRegistrationType && location.state.xMfa) {
      setResetXMfa(location.state.xMfa);
    }
  }, [location.state]);

  const onPinChanged = (pinEntry: number | string, index: number) => {
    const pin = [...newPin];
    pin[index] = pinEntry;
    setNewPin(pin);
    const pinStr = pin.join("");
    setFormData({ ...formData, pin: pinStr });
    updatePinState(pinStr);

    if (
      pinStr.length === PIN_LENGTH &&
      formData.confirmPin.length === PIN_LENGTH
    ) {
      setConfirmPinError(setConfirmPinSignUpValidations(formData.confirmPin, pinStr));
    } else {
      setConfirmPinError({
        message: "",
        hasError: false,
      });
    }
  };

  function updatePinState(pin: string): void {
    if (pin.length === PIN_LENGTH) {
      const sequential = SEQUENTIAL_REGEX.test(pin);
      const repeated = REPEATED_REGEX.test(pin);

      setIsSequential(sequential);
      setIsRepeated(repeated);
    } else {
      setIsSequential(true);
      setIsRepeated(true);
    }
  }

  const onPinBlur = () => {
    setPinError(setPinValidations(formData.pin));

    if (
      formData.pin.length === PIN_LENGTH &&
      formData.confirmPin.length === PIN_LENGTH
    ) {
      setConfirmPinError(
        setConfirmPinSignUpValidations(formData.confirmPin, formData.pin)
      );
    }
  };

  const onConfirmPinChanged = (pinEntry: number | string, index: number) => {
    const confirmPin = [...newConfirmPin];
    confirmPin[index] = pinEntry;
    setConfirmNewPin(confirmPin);
    const confirmPinStr = confirmPin.join("");
    setFormData({ ...formData, confirmPin: confirmPinStr });

    if (
      confirmPinStr.length === PIN_LENGTH &&
      formData.pin.length === PIN_LENGTH
    ) {
      setConfirmPinError(
        setConfirmPinSignUpValidations(confirmPinStr, formData.pin)
      );
    } else {
      setConfirmPinError({
        message: "",
        hasError: false,
      });
    }
  };

  const onConfirmPinBlur = () => {
    setConfirmPinError(
      setConfirmPinSignUpValidations(formData.confirmPin, formData.pin)
    );
  };

  //#region Aux Methods

  const handleBackButton = () => {
    // A facial-originated session has no OTP to go back to - restart at
    // phone entry instead.
    navigateTo(resetJobId ? FORGOT_PIN : MOBILE_VERIFICATION);
  };

  //#endregion Aux Methods

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    UtilityApiService.getPublicKey(DEFAULT_CHANNEL, xApiKey)
      .then((response: ApiResponseDTOEncryptionDTO) => {
        if (!response.data) return;
        if (response.success) {
          const publicKeyBase64 = response.data.publicKeyBase64 || "";

          // Try with SHA-256 first, then fall back to SHA-1, then PKCS1
          let encryptedPin = encrypt(formData.pin, publicKeyBase64, "sha256");
          if (!encryptedPin) {
            encryptedPin = encrypt(formData.pin, publicKeyBase64, "sha1");
            if (!encryptedPin) {
              encryptedPin = encrypt(formData.pin, publicKeyBase64, "pkcs1");
            }
          }

          let confirmEncryptedPin = encrypt(
            formData.confirmPin,
            publicKeyBase64,
            "sha256"
          );
          if (!confirmEncryptedPin) {
            confirmEncryptedPin = encrypt(
              formData.confirmPin,
              publicKeyBase64,
              "sha1"
            );
            if (!confirmEncryptedPin) {
              confirmEncryptedPin = encrypt(
                formData.confirmPin,
                publicKeyBase64,
                "pkcs1"
              );
            }
          }

          // BOAS-7938: exactly one proof goes to the reset call - jobId
          // (facial) or verificationCode (OTP), never both. Only encrypt
          // whichever one this session actually has.
          // BOAS-8726: the team reset carries its proof in X-MFA instead.
          let encryptedVerificationCode = "";
          if (!resetJobId && !resetXMfa) {
            encryptedVerificationCode = encrypt(
              verificationCode,
              publicKeyBase64,
              "sha256"
            ) || "";
            if (!encryptedVerificationCode) {
              encryptedVerificationCode = encrypt(
                verificationCode,
                publicKeyBase64,
                "sha1"
              ) || "";
              if (!encryptedVerificationCode) {
                encryptedVerificationCode = encrypt(
                  verificationCode,
                  publicKeyBase64,
                  "pkcs1"
                ) || "";
              }
            }
          }

          if (
            publicKeyBase64 &&
            publicKeyBase64.length > 0 &&
            encryptedPin &&
            encryptedPin.length > 0 &&
            confirmEncryptedPin &&
            confirmEncryptedPin.length > 0 &&
            (resetJobId ||
              resetXMfa ||
              encryptedVerificationCode.length > 0)
          ) {
            // Log the algorithm used (for debugging)
            console.debug("Passcode encryption succeeded");

            validatePin(
              encryptedPin,
              confirmEncryptedPin,
              encryptedVerificationCode,
              resetJobId
            );
          } else {
            setConfirmPinError({
              message: "Failed to encrypt Passcode. Please try again.",
              hasError: true,
            });
          }
        }
      })
      .catch((error) => {
        throw new Error(error);
      });
  };

  // BOAS-7938 reset-call error codes when a facial jobId was the proof:
  // 404 ZENZ_USER_102 (no such job / wrong purpose / not bound to this
  // phone), 422 ZENZ_USER_103/104/105 (not verified / already used /
  // expired). None of these are fixable by retyping the PIN - the merchant
  // has to redo facial verification, so point them back rather than
  // showing a field error that implies retyping would help.
  const FACIAL_JOB_ERROR_CODES = [
    "ZENZ_USER_102",
    "ZENZ_USER_103",
    "ZENZ_USER_104",
    "ZENZ_USER_105",
  ];

  function validatePin(
    encryptedPin: string,
    confirmEncryptedPin: string,
    encryptedVerificationCode: string,
    jobId: string
  ): void {
    if (resetXMfa) {
      resetPasscodeWithMfa(encryptedPin, confirmEncryptedPin, resetXMfa);
      return;
    }
    UsersApiService.resetPasscode({
      confirmPassCode: confirmEncryptedPin,
      passCode: encryptedPin,
      phoneNumber,
      ...(jobId
        ? { jobId }
        : { verificationCode: encryptedVerificationCode }),
    })
      .then((response) => {
        if ("data" in response && response.internalCode === SUCCESS_API_CODE) {
          setFirstName(response.data?.firstName as string);
          navigateTo(PIN_CREATED);
        }
      })
      .catch((error) => {
        if (
          jobId &&
          FACIAL_JOB_ERROR_CODES.includes(error.body?.internalCode)
        ) {
          setConfirmPinError({
            message:
              "Your facial verification is no longer valid. Please go back and verify again.",
            hasError: true,
          });
          return;
        }

        switch (error.body.internalCode) {
          case "ZENZ-CROSS-004":
            setConfirmPinError({
              message: PIN_MISMATCH_MESSAGE,
              hasError: !error.body.success,
            });
            break;

          default:
            setConfirmPinError({
              message: error.body.message ? error.body.message : "",
              hasError: !error.body.success,
            });
        }
      });
  }

  // BOAS-8726: a phone with no registrationType on record resets through the
  // team passcode endpoint, authorised by the OTP's X-MFA token.
  function resetPasscodeWithMfa(
    encryptedPin: string,
    confirmEncryptedPin: string,
    xMfa: string
  ): void {
    resetTeamPasscode(
      {
        phoneNumber,
        newPasscode: encryptedPin,
        confirmNewPasscode: confirmEncryptedPin,
      },
      xMfa
    )
      .then((response) => {
        if (response.internalCode === SUCCESS_API_CODE) {
          navigateTo(PIN_CREATED);
        }
      })
      .catch((error) => {
        setConfirmPinError({
          message:
            error.body?.internalCode === "ZENZ-CROSS-004"
              ? PIN_MISMATCH_MESSAGE
              : error.body?.message ?? "",
          hasError: !error.body?.success,
        });
      });
  }

  return (
    <div className="flex w-full grow">
      <div className="flex grow flex-col justify-center md:mx-10">
        <div className="flex items-start">
          <Button
            id="go-back-to-mobile-verification"
            startIcon={<ChevronLeft />}
            className="rounded-lg normal-case text-black hover:bg-gray-300"
            onClick={handleBackButton}
          >
            Back
          </Button>
        </div>

        <div className="flex flex-col items-center justify-center">
          <h5 className="mt-16 mb-4 text-4xl">Create New Passcode</h5>
          <span className="text-neutral-600">
            This Passcode will only be used to login to your account
          </span>
        </div>
        <form
          onSubmit={handleSubmit}
          className="mx-3 flex flex-col items-center"
        >
          <div className="mx-auto mt-12 flex w-full max-w-md flex-col">
            <PinInputComponent
              id="create-new-pin"
              label="Create six (6) digit Passcode"
              labelClassName="mb-3 text-neutral-600"
              pinLength={PIN_LENGTH}
              pinContainerClass="gap-2 md:gap-4 xl:gap-6"
              pin={newPin}
              type={"tel"}
              masked={true}
              onPinChanged={onPinChanged}
              onBlur={onPinBlur}
              hasError={pinError.hasError}
              isValidating={false}
              validationMessage={pinError.message}
            />
          </div>

          <div className="mx-auto mt-12 flex w-full max-w-md flex-col">
            <PinInputComponent
              id="create-new-confirm-pin"
              label="Confirm six (6) digit Passcode"
              labelClassName="mb-3 text-neutral-600"
              pinLength={PIN_LENGTH}
              pin={newConfirmPin}
              pinContainerClass="gap-2 md:gap-4 xl:gap-6"
              type={"tel"}
              masked={true}
              onPinChanged={onConfirmPinChanged}
              onBlur={onConfirmPinBlur}
              hasError={confirmPinError.hasError}
              isValidating={false}
              validationMessage={confirmPinError.message}
            />
          </div>
          <PinRulesComponent
            className="mt-6"
            isRepeated={isRepeated}
            isSequential={isSequential}
            necessaryPinLength={PIN_LENGTH}
            pinLength={formData.pin.length}
          />

          <div className="mt-20 flex flex-col items-center space-y-5">
            <MajorButtonComponent
              id="continue-to-pin-created"
              buttonType="primary"
              size="large"
              disabled={
                formData.pin.length !== PIN_LENGTH ||
                formData.confirmPin.length !== PIN_LENGTH ||
                formData.pin !== formData.confirmPin ||
                pinError.hasError ||
                confirmPinError.hasError ||
                isSequential ||
                isRepeated
              }
              type="submit"
            >
              Continue
            </MajorButtonComponent>
          </div>
        </form>
      </div>
    </div>
  );
});

export default CreateNewPinPage;
